/**
 * Микросессия режима «Мои ошибки».
 *
 * Каждое слово проходит Memory Loop целиком: успех двигает цикл вперёд,
 * ошибка откатывает на шаг назад, а не сбрасывает к началу (ТЗ §7.4).
 * Освоенное слово покидает набор и возвращается позже по расписанию
 * интервального повторения.
 */

import type { MasteryUnit, UnitOutcome } from '@type2x/shared-contracts';
import { EMPTY_METRICS } from '@type2x/shared-contracts';
import {
  EN_VOWELS,
  RU_VOWELS,
  advanceMemoryLoop,
  hintLevelFor,
  isMemoryLoopComplete,
  splitIntoChunks,
  startMemoryLoop,
  type MemoryLoopState,
} from '@type2x/learning-engine';

import type {
  MistakeDrill,
  MistakeSnapshot,
  MistakesSession,
  StageRun,
  StageTask,
} from '../contracts/mistakes.js';
import { MAX_CONSECUTIVE_SHOWS, MAX_SHOWS_PER_DRILL, isTypedStage } from '../contracts/mistakes.js';
import { EMPTY_OBSERVATION, mergeMetrics, mergeObservations } from './observation.js';
import { sentenceFor } from './sentences.js';

/** Собирает микросессию из отобранных слов. */
export function startMistakesSession(
  snapshot: MistakeSnapshot,
  units: readonly MasteryUnit[],
): MistakesSession {
  const vowels = snapshot.language === 'ru' ? RU_VOWELS : EN_VOWELS;

  const drills: MistakeDrill[] = units.map((unit) => ({
    unitId: unit.id,
    word: unit.content,
    chunks: splitIntoChunks(unit.content, vowels),
    sentence: sentenceFor(snapshot.language, unit.content),
    loop: startMemoryLoop(unit.content),
    shows: 0,
    completed: false,
    deferred: false,
    observed: EMPTY_OBSERVATION,
  }));

  return {
    language: snapshot.language,
    layoutId: snapshot.layoutId,
    drills,
    currentIndex: 0,
    finished: drills.length === 0,
    metrics: EMPTY_METRICS,
    errors: [],
  };
}

/** Слово, с которым пользователь работает сейчас. */
export function currentDrill(session: MistakesSession): MistakeDrill | null {
  if (session.finished) return null;
  return session.drills[session.currentIndex] ?? null;
}

/** Слова, с которыми микросессия ещё работает. */
export function remainingDrills(session: MistakesSession): MistakeDrill[] {
  return session.drills.filter(isActive);
}

/** Работает ли микросессия с этим словом: оно не освоено и не отложено. */
function isActive(drill: MistakeDrill): boolean {
  return !drill.completed && !drill.deferred;
}

/** Задание текущей стадии либо null, если печатать уже нечего. */
export function stageTask(drill: MistakeDrill): StageTask | null {
  const { stage } = drill.loop;
  if (!isTypedStage(stage)) return null;

  return {
    stage,
    word: drill.word,
    chunks: drill.chunks,
    text: stage === 'apply' ? (drill.sentence ?? drill.word) : drill.word,
    hintLevel: hintLevelFor(stage),
    showsChunks: stage === 'split',
  };
}

/**
 * Переводит цикл текущего слова после прогона стадии.
 *
 * @param session текущая микросессия;
 * @param run прогон стадии.
 */
export function advanceMistakes(session: MistakesSession, run: StageRun): MistakesSession {
  const drill = currentDrill(session);
  if (drill === null) return session;

  const { observation } = run;
  const success = observation.errors === 0;
  const loop = nextLoopState(drill, success);
  const shows = drill.shows + 1;
  const completed = isMemoryLoopComplete(loop);

  const advanced: MistakeDrill = {
    ...drill,
    loop,
    shows,
    completed,
    // Слово, не давшееся за отведённое число показов, откладывается: оно
    // вернётся в следующей микросессии по расписанию повторения.
    deferred: !completed && shows >= MAX_SHOWS_PER_DRILL,
    observed: mergeObservations(drill.observed, observation),
  };

  const drills = session.drills.map((item, index) =>
    index === session.currentIndex ? advanced : item,
  );

  const turn = pickNext(drills, session.currentIndex);

  return {
    ...session,
    drills: turn === null || !turn.resetStreak ? drills : resetStreak(drills, turn.index),
    currentIndex: turn?.index ?? session.currentIndex,
    finished: turn === null,
    metrics: mergeMetrics(session.metrics, run.metrics),
    errors: [...session.errors, ...run.errors],
  };
}

/**
 * Следующее состояние цикла.
 *
 * Слово без естественного предложения пропускает стадию «применить»: цикл
 * не должен требовать материала, которого нет.
 */
function nextLoopState(drill: MistakeDrill, success: boolean): MemoryLoopState {
  const advanced = advanceMemoryLoop(drill.loop, success);
  if (advanced.stage !== 'apply' || drill.sentence !== null) return advanced;

  // Счётчик показов сохраняется: пропуск стадии — не ещё один показ слова.
  return { ...advanceMemoryLoop(advanced, success), consecutiveShows: advanced.consecutiveShows };
}

/** Чей ход дальше и надо ли обнулить счётчик показов подряд. */
interface NextTurn {
  readonly index: number;
  readonly resetStreak: boolean;
}

/**
 * Выбирает, с каким словом работать дальше.
 *
 * Работа остаётся на текущем слове, пока цикл движется и не исчерпан предел
 * показов подряд. Дальше очередь переходит к следующему слову в работе.
 *
 * Если чередовать не с чем — слово осталось последним, — работа продолжается
 * с обнулённым счётчиком: предел существует ради разведения повторов, а не
 * ради того, чтобы оборвать цикл на середине. Бесконечной микросессию это не
 * делает, её длину ограничивает общий предел показов слова.
 */
function pickNext(drills: readonly MistakeDrill[], currentIndex: number): NextTurn | null {
  const current = drills[currentIndex];
  const canStay = current !== undefined && isActive(current);

  if (canStay && current.loop.consecutiveShows < MAX_CONSECUTIVE_SHOWS) {
    return { index: currentIndex, resetStreak: false };
  }

  for (let step = 1; step < drills.length; step += 1) {
    const index = (currentIndex + step) % drills.length;
    if (isActive(drills[index]!)) return { index, resetStreak: true };
  }

  return canStay ? { index: currentIndex, resetStreak: true } : null;
}

/** Обнуляет счётчик показов подряд у слова, к которому перешла очередь. */
function resetStreak(drills: readonly MistakeDrill[], index: number): MistakeDrill[] {
  return drills.map((drill, position) =>
    position === index ? { ...drill, loop: { ...drill.loop, consecutiveShows: 0 } } : drill,
  );
}

/** Агрегаты по единицам микросессии для отправки на сервер. */
export function collectMistakeOutcomes(session: MistakesSession): UnitOutcome[] {
  return session.drills
    .filter((drill) => drill.observed.attempts > 0)
    .map((drill) => ({
      unitId: drill.unitId,
      attempts: drill.observed.attempts,
      errors: drill.observed.errors,
      medianLatencyMs: drill.observed.medianLatencyMs,
      corrections: drill.observed.corrections,
    }));
}
